import React, { useEffect, useMemo, useState } from "react";
import { api, inr, grams } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, Trash, FileText, ListBullets } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [form, setForm] = useState(blankForm());

  function blankForm() {
    return {
      supplier_id: "",
      invoice_no: "",
      invoice_date: new Date().toISOString().slice(0, 10),
      gst_percent: 3,
      paid_amount: 0,
      notes: "",
      items: [
        {
          name: "",
          purity: "",
          gross_weight: 0,
          net_weight: 0,
          quantity: 1,
          rate_per_gram: 0,
          making_charge: 0,
          amount: 0,
        },
      ],
    };
  }

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get("/purchases"),
      api.get("/suppliers"),
    ]);
    setPurchases(p.data);
    setSuppliers(s.data);
  };
  useEffect(() => {
    load();
  }, []);

  const totals = useMemo(() => {
    let sub = 0;
    form.items.forEach((it) => {
      const net = Number(it.net_weight) || Number(it.gross_weight) || 0;
      sub +=
        (net * Number(it.rate_per_gram || 0) + Number(it.making_charge || 0)) *
        Number(it.quantity || 1);
    });
    const gst = (sub * (Number(form.gst_percent) || 0)) / 100;
    return {
      sub,
      gst,
      grand: sub + gst,
      balance: sub + gst - Number(form.paid_amount || 0),
    };
  }, [form]);

  const addItem = () =>
    setForm((f) => ({
      ...f,
      items: [
        ...f.items,
        {
          name: "",
          purity: "",
          gross_weight: 0,
          net_weight: 0,
          quantity: 1,
          rate_per_gram: 0,
          making_charge: 0,
          amount: 0,
        },
      ],
    }));
  const updItem = (i, patch) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, idx) => (idx === i ? { ...it, ...patch } : it)),
    }));
  const rmItem = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.supplier_id) {
      toast.error("Select supplier");
      return;
    }
    if (!form.invoice_no.trim()) {
      toast.error("Invoice number required");
      return;
    }
    if (form.items.length === 0 || !form.items[0].name) {
      toast.error("Add at least one item");
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        gst_percent: Number(form.gst_percent) || 0,
        paid_amount: Number(form.paid_amount) || 0,
        items: form.items.map((it) => ({
          ...it,
          gross_weight: Number(it.gross_weight) || 0,
          net_weight: Number(it.net_weight) || 0,
          quantity: Number(it.quantity) || 1,
          rate_per_gram: Number(it.rate_per_gram) || 0,
          making_charge: Number(it.making_charge) || 0,
        })),
      };
      await api.post("/purchases", payload);
      toast.success("Purchase saved");
      setOpen(false);
      setForm(blankForm());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const viewLedger = async (sid) => {
    const { data } = await api.get(`/suppliers/${sid}/ledger`);
    setLedger(data);
    setLedgerOpen(true);
  };

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="purchases-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Procurement
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-1">
            Purchases
          </h1>
        </div>
        <Button
          data-testid="new-purchase-btn"
          onClick={() => {
            setForm(blankForm());
            setOpen(true);
          }}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus size={16} className="mr-1.5" /> New purchase
        </Button>
      </header>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/40 hover:bg-secondary/40">
              <TableHead>Invoice</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {purchases.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-10 text-muted-foreground"
                >
                  No purchases yet.
                </TableCell>
              </TableRow>
            ) : (
              purchases.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono text-xs">
                    {p.invoice_no}
                  </TableCell>
                  <TableCell className="text-sm">{p.invoice_date}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => viewLedger(p.supplier_id)}
                      className="text-gold hover:underline"
                    >
                      {supName(p.supplier_id)}
                    </button>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(p.grand_total)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(p.paid)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {inr(p.balance)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              New Purchase
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Supplier *
              </Label>
              <Select
                value={form.supplier_id}
                onValueChange={(v) => setForm({ ...form, supplier_id: v })}
              >
                <SelectTrigger
                  data-testid="purchase-supplier"
                  className="mt-1.5 bg-background border-border"
                >
                  <SelectValue placeholder="Choose" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Invoice No *
              </Label>
              <Input
                data-testid="purchase-invoice-no"
                className="mt-1.5 bg-background border-border"
                value={form.invoice_no}
                onChange={(e) =>
                  setForm({ ...form, invoice_no: e.target.value })
                }
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Invoice Date
              </Label>
              <Input
                type="date"
                className="mt-1.5 bg-background border-border"
                value={form.invoice_date}
                onChange={(e) =>
                  setForm({ ...form, invoice_date: e.target.value })
                }
              />
            </div>
          </div>

          <div className="rounded-lg border border-border overflow-hidden mt-4">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                <tr>
                  <th className="text-left px-2 py-2">Item</th>
                  <th>Purity</th>
                  <th>Gross</th>
                  <th>Net</th>
                  <th>Qty</th>
                  <th>Rate/g</th>
                  <th>Making</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1.5">
                      <Input
                        className="h-8 bg-background border-border"
                        value={it.name}
                        onChange={(e) => updItem(i, { name: e.target.value })}
                      />
                    </td>
                    <td className="px-1">
                      <Input
                        className="h-8 bg-background border-border"
                        value={it.purity}
                        onChange={(e) => updItem(i, { purity: e.target.value })}
                      />
                    </td>
                    <td className="px-1">
                      <Input
                        type="number"
                        step="0.001"
                        className="h-8 bg-background border-border text-right tabular-nums"
                        value={it.gross_weight}
                        onChange={(e) =>
                          updItem(i, { gross_weight: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-1">
                      <Input
                        type="number"
                        step="0.001"
                        className="h-8 bg-background border-border text-right tabular-nums"
                        value={it.net_weight}
                        onChange={(e) =>
                          updItem(i, { net_weight: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-1">
                      <Input
                        type="number"
                        className="h-8 w-16 bg-background border-border text-right tabular-nums"
                        value={it.quantity}
                        onChange={(e) =>
                          updItem(i, { quantity: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-1">
                      <Input
                        type="number"
                        step="0.01"
                        className="h-8 bg-background border-border text-right tabular-nums"
                        value={it.rate_per_gram}
                        onChange={(e) =>
                          updItem(i, { rate_per_gram: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-1">
                      <Input
                        type="number"
                        step="0.01"
                        className="h-8 bg-background border-border text-right tabular-nums"
                        value={it.making_charge}
                        onChange={(e) =>
                          updItem(i, { making_charge: e.target.value })
                        }
                      />
                    </td>
                    <td className="px-1 text-right">
                      <button
                        onClick={() => rmItem(i)}
                        className="p-1 text-muted-foreground hover:text-destructive"
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="p-2 border-t border-border">
              <Button variant="ghost" onClick={addItem} className="text-gold">
                <Plus size={14} className="mr-1" /> Add row
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-4">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                GST %
              </Label>
              <Input
                type="number"
                step="0.01"
                className="mt-1.5 bg-background border-border tabular-nums"
                value={form.gst_percent}
                onChange={(e) =>
                  setForm({ ...form, gst_percent: e.target.value })
                }
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">
                Paid amount
              </Label>
              <Input
                type="number"
                step="0.01"
                className="mt-1.5 bg-background border-border tabular-nums"
                value={form.paid_amount}
                onChange={(e) =>
                  setForm({ ...form, paid_amount: e.target.value })
                }
              />
            </div>
            <div className="md:col-span-2 flex flex-col items-end justify-end">
              <div className="text-xs text-muted-foreground">
                Sub:{" "}
                <span className="tabular-nums text-foreground">
                  {inr(totals.sub)}
                </span>{" "}
                · GST:{" "}
                <span className="tabular-nums text-foreground">
                  {inr(totals.gst)}
                </span>
              </div>
              <div className="font-display text-2xl text-gold tabular-nums">
                {inr(totals.grand)}
              </div>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="save-purchase-btn"
              onClick={save}
              loading={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save purchase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={ledgerOpen} onOpenChange={setLedgerOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              Supplier Ledger
            </DialogTitle>
          </DialogHeader>
          {ledger && (
            <div className="space-y-3">
              <div className="text-sm">
                <b>{ledger.supplier.name}</b> · {ledger.supplier.mobile}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  ["Purchase", ledger.total_purchase],
                  ["Paid", ledger.total_paid],
                  ["Outstanding", ledger.outstanding],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-border p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {l}
                    </div>
                    <div className="font-display text-lg mt-1 tabular-nums">
                      {inr(v)}
                    </div>
                  </div>
                ))}
              </div>
              <div className="max-h-72 overflow-y-auto rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/40 hover:bg-secondary/40">
                      <TableHead>Inv</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Paid</TableHead>
                      <TableHead className="text-right">Bal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.purchases.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={5}
                          className="text-center py-6 text-muted-foreground"
                        >
                          No purchases.
                        </TableCell>
                      </TableRow>
                    ) : (
                      ledger.purchases.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-mono text-xs">
                            {p.invoice_no}
                          </TableCell>
                          <TableCell className="text-sm">
                            {p.invoice_date}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {inr(p.grand_total)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {inr(p.paid)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {inr(p.balance)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
